import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  message,
  Spin,
  Button,
  Select,
  Tooltip,
  Badge,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PrinterOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownSquareOutlined,
  UpSquareOutlined,
  UserOutlined,
  CommentOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import Search from "antd/es/input/Search";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import MotionHoc from "../../../utils/MotionHoc";
import {
  baseUrl,
  HEADERS_EXPORT_BEN,
  POST_BILL_DETAIL,
  POST_MAIN_ADVANCE_PAY,
  POST_PRINT_ADVANCE_PAY_DB2,
  POST_PRINT_FLAG,
} from "../../API/apiUrls";
import axios from "axios";
import DateCustom from "../../../hook/DateCustom";
import dayjs from "dayjs";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import useLawyerNames from "../../../hook/UseLawyerName";
import { generatePDF } from "./report/AdvancePaymentAndBillPDF";
import { ThaiRangePicker } from "../../../hook/ThaiDatePicker";
import ExpenseApprovalModal from "./modal/ExpenseApprovalModal";
import LoadCompanies from "../../../hook/LoadCompanies";
import "../../../assets/styles/Variables.css";

const { Option } = Select;

const Main = () => {
  // ============== HOOKS ==============
  const [convertDateThai, convertDateThaiShort] = DateCustom();
  const {
    companiesList,
    companiesOptions: allCompaniesOptions,
    loading: loadingCompanies,
  } = LoadCompanies();
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const { nameLawyers, loadingUseLawyerNames } = useLawyerNames({
    auto: true,
    toast: false,
  });

  const { Title, Text } = Typography;

  // ============== LOCAL STORAGE ==============
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const userCompany = localStorage.getItem("COMPANY_ID");

  // ============== STATES ==============
  const [arrayTable, setArrayTable] = useState([]);
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Filter States
  const [lawyerId, setLawyerId] = useState(null);
  const [lawyerName, setLawyerName] = useState(null);
  const [statusId, setStatusId] = useState(ROLE_ID === "6" ? 1 : 0);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  // UI States
  const [advanceSearch, setAdvanceSearch] = useState(false);
  const [arrow] = useState("Show");

  // Image States
  const [imageList, setImageList] = useState([]);

  // Modal States
  const [isModal, setIsModal] = useState(false);
  const [dataRecord, setDataRecord] = useState(null);

  const toggleAdvanceSearch = () => setAdvanceSearch((v) => !v);

  // ============== COMPANIES OPTIONS ==============
  const companiesOptions = useMemo(() => {
    if (!allCompaniesOptions || allCompaniesOptions.length === 0) return [];
    return allCompaniesOptions.filter((item) => item.value <= 3);
  }, [allCompaniesOptions]);

  // ============== LAWYER FILTERS ==============
  const filterLawyersByCompany = useCallback((lawyers, userCompany) => {
    if (!Array.isArray(lawyers) || !userCompany) return [];
    return lawyers.filter(
      (item) =>
        (item.ROLE_ID === 3 || item.ROLE_ID === 4) && item.ACTIVE_STATUS === 1,
    );
  }, []);

  const transformLawyersToOptions = useCallback((lawyers) => {
    return lawyers.map((item) => ({
      value: item.id,
      label: item.NNAME,
      data: item,
    }));
  }, []);

  const lawyersOption = useMemo(() => {
    if (!nameLawyers?.length || !userCompany) return [];
    const filtered = filterLawyersByCompany(nameLawyers, userCompany);
    return [...transformLawyersToOptions(filtered)];
  }, [
    nameLawyers,
    userCompany,
    filterLawyersByCompany,
    transformLawyersToOptions,
  ]);

  const mergedArrow = useMemo(() => {
    if (arrow === "Hide") return false;
    if (arrow === "Show") return true;
    return { pointAtCenter: true };
  }, [arrow]);

  // ============== LOAD DATA ==============
  const handleSearch = async () => {
    await loadData();
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    console.log("userId", userId);

    const payload = {
      userId: userId,
      approvalStatus: statusId,
    };

    console.log("Load data payload:", payload);

    try {
      const response = await axios.post(POST_MAIN_ADVANCE_PAY, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (response.data && Array.isArray(response.data)) {
        console.log("API Response:", response.data);
        const newData = response.data.map((item, index) => ({
          ...item,
          key: index,
        }));
        processData(newData);
        setHasSearched(true);
        setAdvanceSearch(true);
      } else {
        setArrayTable([]);
        setDataArr([]);
        message.warning("ไม่พบข้อมูล");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
      setArrayTable([]);
      setDataArr([]);
    } finally {
      setLoading(false);
    }
  }, [statusId, userId]);

  const loadTMBill = async (values) => {
    console.log("Loading TMBILL data for:", values);
    if (!Array.isArray(values) || values.length === 0) {
      message.warning("ไม่มีข้อมูลที่จะโหลด");
      return [];
    }

    const TMBillData = values.map((item) => item.TMBILL);
    const payload = { TMBILL: TMBillData };
    console.log("Load TMBILL payload:", payload);

    try {
      const response = await axios.post(POST_BILL_DETAIL, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (response.data && Array.isArray(response.data)) {
        console.log("TMBILL API Response:", response.data);
        return response.data.map((item, index) => ({ ...item, key: index }));
      } else {
        message.warning("ไม่พบข้อมูลรายละเอียดบิล");
        return [];
      }
    } catch (error) {
      console.error("Error loading TMBILL data:", error);
      message.error(`เกิดข้อผิดพลาดในการโหลดข้อมูลบิล: ${error.message}`);
      return [];
    }
  };

  useEffect(() => {
    loadData();
    loadImagesList();
  }, [loadData]);

  // ============== PROCESS DATA ==============
  const processData = useCallback(
    (rawData) => {
      if (!Array.isArray(rawData) || rawData.length === 0) {
        setArrayTable([]);
        setDataArr([]);
        return;
      }

      const filtered = rawData.filter((item) => {
        const matchStatus = item.approvalStatus === statusId;
        const hasPermission =
          ROLE_ID === "1" ||
          ROLE_ID === "6" ||
          ROLE_ID === "12" ||
          ROLE_ID === "14";
        return matchStatus && hasPermission;
      });

      console.log("Filtered data:", filtered);

      const sorted = filtered.sort(
        (a, b) => dayjs(b.payamtDate).valueOf() - dayjs(a.payamtDate).valueOf(),
      );

      setDataArr(sorted);
      setArrayTable(sorted);
    },
    [statusId, ROLE_ID],
  );

  // ============== CHECK ACTIVE FILTERS ==============
  const hasActiveFilters = useMemo(
    () =>
      searchText.trim() !== "" ||
      dateRange !== null ||
      lawyerId !== null ||
      companyId !== null,
    [searchText, dateRange, lawyerId, companyId],
  );

  // ============== FILTER FUNCTIONS ==============
  const applyFilters = useCallback(() => {
    let filtered = [...dataArr];

    console.log("Applying filters:", {
      searchText,
      dateRange,
      lawyerId,
      companyId,
      statusId,
    });

    if (searchText && searchText.trim() !== "") {
      const search = searchText.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          item.TMBILL?.toLowerCase().includes(search) ||
          item.payamtMark?.toLowerCase().includes(search),
      );
    }

    if (dateRange && dateRange.length === 2) {
      const startDate = dayjs(dateRange[0]).startOf("day").valueOf();
      const endDate = dayjs(dateRange[1]).endOf("day").valueOf();
      filtered = filtered.filter((item) => {
        let dateToCheck;
        if (item.approvalStatus === 0) dateToCheck = item.payamtDate;
        else if (item.approvalStatus === 1 || item.approvalStatus === 2)
          dateToCheck = item.approvalDate;
        if (!dateToCheck) return false;
        const itemDate = dayjs(dateToCheck).valueOf();
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    if (companyId !== null)
      filtered = filtered.filter((item) => item.COMPANY_ID === companyId);

    if (lawyerId !== null)
      filtered = filtered.filter((item) => item.userId === lawyerId);

    console.log("Final filtered count:", filtered.length);
    setArrayTable(filtered);

    if (hasActiveFilters)
      message.success(`กรองข้อมูลเรียบร้อย พบ ${filtered.length} รายการ`);
  }, [
    dataArr,
    searchText,
    dateRange,
    lawyerId,
    companyId,
    statusId,
    hasActiveFilters,
  ]);

  // ============== IMAGE HANDLING ==============
  const loadImagesList = async () => {
    try {
      const response = await axios.get(
        baseUrl + `/files/lawyer/user/license_lawyer/public`,
      );
      console.log("ImageList", response.data);
      setImageList(response.data);
    } catch (err) {
      console.log("Error loading images:", err);
    }
  };

  const renderLawyer = async (value) => {
    const expectedName = `lawyer/user/license_lawyer/public/${value}.png`;
    const lawyerLicense = imageList?.find((item) => item.name === expectedName);
    if (!lawyerLicense) return;
  };

  // ============== EVENT HANDLERS ==============
  const onChangeSelectCompany = (value) => {
    console.log("Company changed:", value);
    setCompanyId(value);
  };

  const onChangeSelectLawyer = (value, option) => {
    console.log("Lawyer changed:", value, option);
    if (!value) {
      setLawyerName(null);

      setLawyerId(null);
    } else {
      setLawyerName(option.data);
      setLawyerId(value);
      if (imageList.length > 0) renderLawyer(value);
    }
  };

  const onChangeSelectStatus = (value) => {
    console.log("Status changed:", value);
    setStatusId(value);
    resetAllFilters();
  };

  const onSearch = (e) => {
    const value = e.target.value;
    console.log("Search text:", value);
    setSearchText(value);
  };

  const onSearchByDate = (dates) => {
    console.log("Date range changed:", dates);
    if (!dates || dates.length === 0) {
      setDateRange(null);
      return;
    }
    setDateRange(dates);
  };

  const handleApplyFilters = () => {
    console.log("Applying filters manually...");
    applyFilters();
  };

  const handleReset = () => {
    console.log("Resetting filters...");
    setSearchText("");
    setDateRange(null);
    setLawyerId(null);
    setLawyerName(null);
    setCompanyId(null);
    setArrayTable(dataArr);
    message.info("รีเซ็ตตัวกรองเรียบร้อย");
  };

  const resetAllFilters = () => {
    console.log("Resetting all filters and data...");
    setArrayTable([]);
    setDataArr([]);
    setHasSearched(false);
    setAdvanceSearch(false);
    setSearchText("");
    setDateRange(null);
    setLawyerId(null);
    setLawyerName(null);
    setCompanyId(null);
  };

  // โหลดข้อมูลใบเสร็จจาก DOCNO — return data หรือ null
  const postPrintAdvancePayDB2 = async (record) => {
    console.log("record========>", record);
    const payload = { DOCNO: record.DOCNO };
    console.log("payload", payload);

    try {
      const res = await axios.post(POST_PRINT_ADVANCE_PAY_DB2, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      const { status, data } = res;
      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
      return null;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;
      if (status === 404) message.error("ไม่พบปลายทาง (404)");
      else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      else if (status === 500)
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      else
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      console.error("❌ postPrintAdvancePayDB2 error:", error);
      return null;
    }
  };

  // ============== API CALLS ==============
  const postPrintFlag = useCallback(
    async (record) => {
      const payload = { flagPrint: 1, TMBILL: [record.TMBILL], userId };
      try {
        const { data } = await axios.post(POST_PRINT_FLAG, payload, {
          headers: HEADERS_EXPORT_BEN,
        });
        return data;
      } catch (error) {
        const status = error?.response?.status;
        const serverMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error.message;
        if (status === 404) message.error("ไม่พบปลายทาง (404)");
        else if (status === 400)
          message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
        else if (status === 500)
          message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
        else
          message.error(
            `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
          );
        console.error("❌ postPrintFlag error:", error);
      }
    },
    [userId],
  );

  // ============== PDF PRINT ==============
  const handlePrintPDF = useCallback(
    async (record, { onSuccess, onError } = {}) => {
      setLoading(true);
      try {
        const [billDetail] = await Promise.all([
          loadTMBill([record]),
          postPrintFlag(record),
        ]);

        if (!billDetail?.length) {
          message.error("ไม่พบข้อมูลรายละเอียดบิล");
          onError?.("ไม่พบข้อมูลรายละเอียดบิล");
          return;
        }

        const advancePayData = record.DOCNO
          ? await postPrintAdvancePayDB2(record)
          : null;
        console.log("advancePayData", advancePayData);

        await generatePDF({
          dataExport: [record],
          selectedRows: [record],
          billDetail,
          nameLawyers,
          companiesList,
          imageList,
          convertDateThai,
          currencyFormatPoint,
          advancePayData,
        });

        message.success("กำลังเปิดหน้าต่างพิมพ์...");
        onSuccess?.(record);
      } catch (error) {
        console.error("Error generating PDF:", error);
        message.error("เกิดข้อผิดพลาดในการสร้าง PDF");
        onError?.(error);
      } finally {
        setLoading(false);
      }
    },
    [
      nameLawyers,
      companiesList,
      imageList,
      convertDateThai,
      currencyFormatPoint,
      postPrintFlag,
    ],
  );

  // ============== MODAL CALLBACK ==============
  const handleModalCallback = useCallback(() => {
    console.log("📥 Callback from modal");
    if (ROLE_ID === "6") setStatusId(1);
    loadData();
    setIsModal(false);
    setDataRecord(null);
  }, [loadData, ROLE_ID]);

  // ============== RENDER FUNCTIONS ==============
  const renderDate = useCallback((date, status) => {
    if (!date) return <Tag>-</Tag>;

    const recordDate = dayjs(date);
    const today = dayjs().startOf("day");
    const daysDifference = today.diff(recordDate, "day");

    let color = "blue";
    if (status === 1) color = "green";
    else if (status === 2) color = "red";
    else if (daysDifference > 7) color = "red";

    const formattedDate = dayjs(recordDate).format("DD MMM YYYY");

    return (
      <Tag color={color} style={{ textAlign: "center" }}>
        {formattedDate}
        {status === 0 && (
          <>
            <br />
            {daysDifference > 7 && "นาน"} {daysDifference} วัน
          </>
        )}
      </Tag>
    );
  }, []);

  const renderStatus = (status) => {
    const statusMap = {
      0: { text: "รอดำเนินการ", color: "blue", icon: "🕒" },
      1: { text: "อนุมัติ", color: "green", icon: <CheckCircleOutlined /> },
      2: { text: "ไม่อนุมัติ", color: "red", icon: <CloseCircleOutlined /> },
    };
    const { text, color, icon } = statusMap[status] || statusMap[0];
    return (
      <Tag color={color}>
        {typeof icon === "string" ? (
          <span style={{ marginRight: 4 }}>{icon}</span>
        ) : (
          React.cloneElement(icon, { style: { marginRight: 4 } })
        )}
        {text}
      </Tag>
    );
  };

  const renderManage = useCallback(
    (record) => (
      <Space size="small">
        <Button
          size="small"
          type="primary"
          danger={record.approvalStatus === 1 && !record.DOCNO}
          icon={<CheckCircleOutlined />}
          onClick={() => {
            setDataRecord(record);
            setIsModal(true);
          }}
        >
          {record.approvalStatus === 0
            ? "อนุมัติ"
            : record.approvalStatus === 1 && !record.DOCNO
              ? "รอตั้งเบิก"
              : "ดูข้อมูล"}
        </Button>

        <Tooltip title="พิมพ์ข้อมูลแถวนี้" arrow={mergedArrow}>
          <Button
            size="small"
            icon={<PrinterOutlined />}
            disabled={record.approvalStatus === 2}
            onClick={() =>
              handlePrintPDF(record, {
                onSuccess: () => loadData(), // reload หลังพิมพ์สำเร็จ
              })
            }
          >
            พิมพ์
          </Button>
        </Tooltip>
      </Space>
    ),
    [handlePrintPDF, mergedArrow, loadData],
  );

  const renderOptionStatus = () => (
    <>
      <Option value={0}>
        <span style={{ marginRight: 8 }}>🕒</span>รอดำเนินการ
      </Option>
      <Option value={1}>
        <CheckCircleOutlined style={{ color: "green", marginRight: 8 }} />
        อนุมัติ
      </Option>
      <Option value={2}>
        <CloseCircleOutlined style={{ color: "red", marginRight: 8 }} />
        ไม่อนุมัติ
      </Option>
    </>
  );

  // ============== TABLE COLUMNS ==============
  const columns = useMemo(
    () =>
      [
        {
          title: "ลำดับ",
          dataIndex: "key",
          key: "key",
          align: "center",
          width: "5%",
          render: (text, record, index) => index + 1,
        },
        {
          title: "เลขที่อ้างอิง",
          align: "center",
          width: "20%",
          render: (text, record) => (
            <strong style={{ color: "#1890ff" }}>
              เลขบิล: {record.TMBILL}
              <br />
              <Text style={{ color: record.DOCNO ? "green" : "red" }}>
                {" "}
                เลขตั้งเบิก: {record.DOCNO || "รอตั้งเบิก"}
              </Text>
            </strong>
          ),
        },
        (ROLE_ID === "1" || ROLE_ID === "14") && {
          title: "วันที่ขอเบิก",
          dataIndex: "payamtDate",
          key: "payamtDate",
          align: "center",
          width: "10%",
          render: (date, record) => renderDate(date, record.approvalStatus),
        },
        {
          title: "จำนวนเงิน",
          dataIndex: "payamt",
          key: "payamt",
          align: "right",
          width: "10%",
          render: (amount) => (
            <strong style={{ color: "#1890ff", fontSize: "14px" }}>
              {currencyFormatPoint(amount)} ฿
            </strong>
          ),
        },
        {
          title: "วันที่อนุมัติ",
          dataIndex: "approvalDate",
          key: "approvalDate",
          align: "center",
          width: "10%",
          render: (date, record) =>
            date ? renderDate(date, record.approvalStatus) : <Tag>-</Tag>,
        },
        (ROLE_ID === "1" || ROLE_ID === "14") && {
          title: "สถานะ",
          dataIndex: "approvalStatus",
          key: "approvalStatus",
          align: "center",
          width: "10%",
          render: (status) => renderStatus(status),
        },
        (ROLE_ID === "1" || ROLE_ID === "6") && {
          title: "วันที่ตั้งเบิก",
          dataIndex: "DOCDT",
          key: "DOCDT",
          align: "center",
          width: "10%",
          render: (date, record) => renderDate(date, record.DOCDT),
        },
        {
          title: "ผู้ขอเบิก",
          align: "center",
          width: "10%",
          render: (record) => {
            const lawyer = nameLawyers?.find(
              (item) => item.id === record?.userId,
            );
            return (
              <Tag
                color="blue"
                icon={<UserOutlined />}
                style={{
                  fontSize: "16px",
                  padding: "4px 12px",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {lawyer?.NNAME || "-"}
              </Tag>
            );
          },
        },
        {
          title: "หมายเหตุ",
          align: "center",
          width: "10%",
          render: (record) => {
            if (!record.allNote || record.allNote.length === 0) return "-";
            const tooltipContent = (
              <div>
                {record.allNote.map((noteItem, index) => (
                  <div
                    key={noteItem.id || index}
                    style={{ marginBottom: "8px" }}
                  >
                    <p
                      style={{ color: "white", margin: 0, fontWeight: "bold" }}
                    >
                      {noteItem.note}
                    </p>
                    <p
                      style={{
                        color: "#d9d9d9",
                        fontSize: "12px",
                        margin: "4px 0 0",
                      }}
                    >
                      โดย: {noteItem.USERNAME}
                    </p>
                    <p
                      style={{
                        color: "#d9d9d9",
                        fontSize: "12px",
                        margin: "4px 0 0",
                      }}
                    >
                      {dayjs(noteItem.CreatedAt).format("DD/MM/YYYY HH:mm")}
                    </p>
                  </div>
                ))}
              </div>
            );
            return (
              <Tooltip title={tooltipContent}>
                <Space>
                  <CommentOutlined style={{ color: "#1890ff" }} />
                  <Badge count={record.allNote.length} />
                </Space>
              </Tooltip>
            );
          },
        },
        {
          title: "การจัดการ",
          key: "action",
          align: "center",
          width: "20%",
          fixed: "right",
          render: (_, record) => renderManage(record),
        },
      ].filter(Boolean),
    [
      currencyFormatPoint,
      nameLawyers,
      ROLE_ID,
      renderDate,
      renderManage,
      userId,
    ],
  );

  // ============== CALCULATE TOTALS ==============
  const totalAmount = useMemo(
    () =>
      arrayTable.reduce((sum, item) => sum + (parseInt(item.payamt) || 0), 0),
    [arrayTable],
  );

  // ============== GET DATE FIELD NAME ==============
  const getDateFieldLabel = useMemo(() => {
    if (statusId === 0) return "วันที่ขอเบิก";
    if (statusId === 1 || statusId === 2) return "วันที่อนุมัติ";
    return "วันที่";
  }, [statusId]);

  // ============== RENDER UI ==============
  return (
    <Card>
      <Spin
        spinning={loading || loadingCompanies}
        size="large"
        tip="กำลังโหลดข้อมูล..."
      >
        <Row gutter={[16, 16]}>
          {/* ============== SEARCH BAR ============== */}
          <Col span={24}>
            <Card
              size="small"
              style={{
                background: "rgba(0, 57, 96, 0.08)",
                border: "1px solid rgba(0, 57, 96, 0.15)",
              }}
            >
              <Row gutter={[8, 8]} align="middle">
                <Col>
                  <Select
                    placeholder="เลือกสถานะ"
                    onChange={onChangeSelectStatus}
                    value={statusId}
                    style={{ width: 180, fontSize: "var(--font-input)" }}
                    size="large"
                    disabled={ROLE_ID === "6"}
                  >
                    {renderOptionStatus()}
                  </Select>
                </Col>

                <Col>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    size="large"
                    onClick={handleSearch}
                    loading={loading}
                    style={{ minWidth: 120, fontSize: "var(--font-body)" }}
                  >
                    ค้นหา
                  </Button>
                </Col>

                {hasSearched && (
                  <Col>
                    <Tooltip
                      title={advanceSearch ? "ซ่อนตัวกรอง" : "แสดงตัวกรอง"}
                    >
                      <Button
                        icon={
                          advanceSearch ? (
                            <UpSquareOutlined />
                          ) : (
                            <DownSquareOutlined />
                          )
                        }
                        onClick={toggleAdvanceSearch}
                        size="large"
                      >
                        {advanceSearch ? "ซ่อน" : "แสดง"}ตัวกรอง
                      </Button>
                    </Tooltip>
                  </Col>
                )}
              </Row>
            </Card>
          </Col>

          {/* ============== ADVANCED FILTERS ============== */}
          {hasSearched && advanceSearch && (
            <Col span={24}>
              <Card
                size="small"
                style={{ background: "#f0f5ff", marginBottom: 0 }}
              >
                <Row gutter={[8, 8]} align="middle" wrap>
                  <Col>
                    <Select
                      placeholder="เลือกทนายความ"
                      options={lawyersOption}
                      loading={loadingUseLawyerNames}
                      showSearch
                      optionFilterProp="label"
                      value={lawyerId}
                      onChange={onChangeSelectLawyer}
                      style={{ width: 180 }}
                      size="large"
                      allowClear
                    />
                  </Col>

                  <Col>
                    <Tooltip title={`กรองตาม ${getDateFieldLabel}`}>
                      <ThaiRangePicker
                        style={{ width: 300 }}
                        placeholder={["วันที่เริ่มต้น", "วันที่สิ้นสุด"]}
                        size="large"
                        onChange={onSearchByDate}
                        value={dateRange}
                      />
                    </Tooltip>
                  </Col>

                  <Col>
                    <Select
                      placeholder="เลือกบริษัท"
                      showSearch
                      optionFilterProp="label"
                      options={companiesOptions}
                      onChange={onChangeSelectCompany}
                      value={companyId}
                      style={{ width: 250 }}
                      popupMatchSelectWidth={false}
                      allowClear
                      size="large"
                      loading={loadingCompanies}
                    />
                  </Col>

                  <Col>
                    <Search
                      placeholder="ค้นหาเลขที่อ้างอิง"
                      onChange={onSearch}
                      value={searchText}
                      enterButton={<SearchOutlined />}
                      style={{ width: 280 }}
                      size="large"
                      allowClear
                    />
                  </Col>

                  <Col>
                    <Tooltip title="กดเพื่อกรองข้อมูลตามเงื่อนไขที่เลือก">
                      <Button
                        type="primary"
                        icon={<FilterOutlined />}
                        onClick={handleApplyFilters}
                        disabled={!hasActiveFilters}
                        size="large"
                      >
                        กรองข้อมูล
                      </Button>
                    </Tooltip>
                  </Col>

                  <Col>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleReset}
                      size="large"
                    >
                      รีเซ็ต
                    </Button>
                  </Col>
                </Row>

                {hasActiveFilters && dateRange && (
                  <Row style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <Tag color="orange" style={{ fontSize: "12px" }}>
                        ℹ️ กรองตาม{" "}
                        <strong>
                          {statusId === 0 ? "วันที่ขอเบิก" : "วันที่อนุมัติ"}
                        </strong>
                      </Tag>
                    </Col>
                  </Row>
                )}
              </Card>
            </Col>
          )}

          {/* ============== FILTER SUMMARY ============== */}
          {hasSearched && hasActiveFilters && (
            <Col span={24}>
              <Card size="small" style={{ background: "#fffbe6" }}>
                <Space split="|" size="large" wrap>
                  <span style={{ fontSize: "var(--font-body)" }}>
                    <strong>ผลการค้นหา:</strong>{" "}
                    <Tag color="blue">{arrayTable.length} รายการ</Tag>
                  </span>
                  {searchText && (
                    <span>
                      <strong>คำค้นหา:</strong> "{searchText}"
                    </span>
                  )}
                  {dateRange && (
                    <span>
                      <strong>ช่วงวันที่:</strong>{" "}
                      {dayjs(dateRange[0])
                        .subtract(543, "year")
                        .format("D MMM YY")}{" "}
                      -{" "}
                      {dayjs(dateRange[1])
                        .subtract(543, "year")
                        .format("D MMM YY")}
                      <Tag
                        color="geekblue"
                        size="small"
                        style={{ marginLeft: 8 }}
                      >
                        {statusId === 0 ? "วันที่ขอเบิก" : "วันที่อนุมัติ"}
                      </Tag>
                    </span>
                  )}
                  {lawyerId !== null && lawyerName && (
                    <span>
                      <strong>ทนาย:</strong> {lawyerName.NNAME}
                    </span>
                  )}
                  {companyId !== null && (
                    <span>
                      <strong>บริษัท:</strong>{" "}
                      {companiesOptions.find((opt) => opt.value === companyId)
                        ?.label || "-"}
                    </span>
                  )}
                </Space>
              </Card>
            </Col>
          )}

          {/* ============== TABLE ============== */}
          {hasSearched && (
            <Col span={24}>
              <Table
                columns={columns}
                dataSource={arrayTable}
                rowKey="key"
                size="middle"
                scroll={{ x: 1500 }}
                loading={loading}
                bordered
                pagination={{
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `แสดง ${range[0]}-${range[1]} จาก ${total} รายการ`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  position: ["bottomCenter"],
                }}
                footer={() => (
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Space split="|" size="middle">
                        <span style={{ fontWeight: 500 }}>
                          จำนวนทั้งหมด: {arrayTable.length} รายการ
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          ยอดรวมทั้งหมด:{" "}
                          <strong style={{ color: "#1890ff" }}>
                            {currencyFormatPoint(totalAmount)} บาท
                          </strong>
                        </span>
                      </Space>
                    </Col>
                  </Row>
                )}
              />
            </Col>
          )}

          {/* ============== EMPTY STATE ============== */}
          {!hasSearched && (
            <Col span={24}>
              <div
                style={{
                  textAlign: "center",
                  padding: "80px 20px",
                  background: "#fafafa",
                  borderRadius: "8px",
                }}
              >
                <SearchOutlined
                  style={{ fontSize: "var(--font-title)", color: "#d9d9d9" }}
                />
                <p
                  style={{
                    marginTop: "24px",
                    fontSize: "var(--font-sub-title)",
                    color: "#666",
                  }}
                >
                  กรุณาเลือกเงื่อนไขและกดปุ่ม "ค้นหา" เพื่อแสดงข้อมูล
                </p>
                <p style={{ fontSize: "var(--font-body)", color: "#999" }}>
                  เลือกสถานะที่ต้องการค้นหา
                </p>
              </div>
            </Col>
          )}
        </Row>
      </Spin>

      {/* ============== MODAL ============== */}
      {isModal && (
        <ExpenseApprovalModal
          open={isModal}
          close={() => {
            setIsModal(false);
            setDataRecord(null);
          }}
          dataDefault={dataRecord}
          funcUpdateStatus={handleModalCallback}
          nameLawyers={nameLawyers}
        />
      )}
    </Card>
  );
};

const MainManageWithdrawAdvancePayment = MotionHoc(Main);
export default MainManageWithdrawAdvancePayment;
